import { useCallback, useContext, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { SIndexTableContext, SIndexTableSelectionChangeContext } from './context';
import type {
    SIndexTableBulkSelectionDataOptions,
    SIndexTableHandleBulkSelectionOptions,
    SIndexTableHandleSelectionChange,
    SIndexTableRange,
} from './types';
import { S_INDEX_TABLE_SELECT_ALL_ITEMS, SIndexTableSelectionType } from './types';

export function useSIndexTableSelectionChange() {
    const onSelectionChange = useContext(SIndexTableSelectionChangeContext);
    if (!onSelectionChange) {
        throw new Error(`Missing SIndexTableProvider context`);
    }
    return onSelectionChange;
}

export function useSIndexTableContext() {
    const index = useContext(SIndexTableContext);
    if (!index) {
        throw new Error(`Missing SIndexTableProvider context`);
    }
    return index;
}

export function useSIndexTableBulkSelectionData({
    selectedItemsCount,
    itemCount,
    hasMoreItems,
    resourceName: passedResourceName,
    defaultPaginatedSelectAllText,
}: SIndexTableBulkSelectionDataOptions) {
    const { t } = useTranslation('polaris');
    const selectable = Boolean(selectedItemsCount);
    const selectMode = selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS || selectedItemsCount > 0;

    const defaultResourceName = {
        singular: t('Polaris.IndexProvider.defaultItemSingular'),
        plural: t('Polaris.IndexProvider.defaultItemPlural'),
    };

    const resourceName = passedResourceName ? passedResourceName : defaultResourceName;

    const paginatedSelectAllText = getPaginatedSelectAllText();

    const bulkActionsLabel = getBulkActionsLabel();
    const bulkActionsAccessibilityLabel = getBulkActionsAccessibilityLabel();

    let bulkSelectState: boolean | 'indeterminate' | undefined = 'indeterminate';
    if (!selectedItemsCount || selectedItemsCount === 0) {
        bulkSelectState = undefined;
    } else if (selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS || selectedItemsCount === itemCount) {
        bulkSelectState = true;
    }

    return {
        paginatedSelectAllText,
        bulkActionsLabel,
        bulkActionsAccessibilityLabel,
        resourceName,
        selectMode,
        bulkSelectState,
        selectable,
    };

    function getPaginatedSelectAllText() {
        if (!selectable || !hasMoreItems) {
            return;
        }

        if (selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS) {
            if (defaultPaginatedSelectAllText) {
                return defaultPaginatedSelectAllText;
            }
            return t('Polaris.IndexProvider.allItemsSelected', {
                itemsLength: itemCount,
                resourceNamePlural: resourceName.plural.toLocaleLowerCase(),
            });
        }
    }

    function getBulkActionsLabel() {
        const selectedItemsCountLabel =
            selectedItemsCount === S_INDEX_TABLE_SELECT_ALL_ITEMS ? `${itemCount}+` : selectedItemsCount;

        return t('Polaris.IndexProvider.selected', {
            selectedItemsCount: selectedItemsCountLabel,
        });
    }

    function getBulkActionsAccessibilityLabel() {
        const totalItemsCount = itemCount;
        const allSelected = selectedItemsCount === totalItemsCount;

        if (totalItemsCount === 1 && allSelected) {
            return t('Polaris.IndexProvider.a11yCheckboxDeselectAllSingle', {
                resourceNameSingular: resourceName.singular,
            });
        } else if (totalItemsCount === 1) {
            return t('Polaris.IndexProvider.a11yCheckboxSelectAllSingle', {
                resourceNameSingular: resourceName.singular,
            });
        } else if (allSelected) {
            return t('Polaris.IndexProvider.a11yCheckboxDeselectAllMultiple', {
                itemsLength: itemCount,
                resourceNamePlural: resourceName.plural,
            });
        } else {
            return t('Polaris.IndexProvider.a11yCheckboxSelectAllMultiple', {
                itemsLength: itemCount,
                resourceNamePlural: resourceName.plural,
            });
        }
    }
}

export function useSIndexTableHandleBulkSelection({
    onSelectionChange = () => {},
}: SIndexTableHandleBulkSelectionOptions) {
    const lastSelected = useRef<number | null>(null);

    const handleSelectionChange: SIndexTableHandleSelectionChange = useCallback(
        (
            selectionType: SIndexTableSelectionType,
            toggleType: boolean,
            selection?: string | SIndexTableRange,
            sortOrder?: number
        ) => {
            const prevSelected = lastSelected.current;

            if (SIndexTableSelectionType.Multi && typeof sortOrder === 'number') {
                lastSelected.current = sortOrder;
            }

            if (
                selectionType === SIndexTableSelectionType.Single ||
                (selectionType === SIndexTableSelectionType.Multi &&
                    (typeof prevSelected !== 'number' || typeof sortOrder !== 'number'))
            ) {
                onSelectionChange(SIndexTableSelectionType.Single, toggleType, selection);
            } else if (selectionType === SIndexTableSelectionType.Multi) {
                const min = Math.min(prevSelected as number, sortOrder as number);
                const max = Math.max(prevSelected as number, sortOrder as number);
                onSelectionChange(selectionType, toggleType, [min, max]);
            } else if (
                selectionType === SIndexTableSelectionType.Page ||
                selectionType === SIndexTableSelectionType.All
            ) {
                onSelectionChange(selectionType, toggleType);
            } else if (selectionType === SIndexTableSelectionType.Range) {
                onSelectionChange(SIndexTableSelectionType.Range, toggleType, selection);
            }
        },
        [onSelectionChange]
    );

    return handleSelectionChange;
}
